import Price from '@/components/Price';
import { placeHolderImage } from '@/constants/placeHolderImage';
import { useIsPreorder } from '@/hooks/MiniCart/useIsPreorder';
import { formatString } from '@/lib/format-string';

import { useMiniCartStore } from '@/components/MiniCart/use-new-minicart-store';
import { ConfigurableProduct } from '@/types/graphql.prod';
import resourceUrl from '@/util/makeUrl';
import Image from 'next/image';
import { useMemo } from 'react';
import { AspectRatio } from '../ui/aspectRatio';
import { CustomizeOptions } from './CustomizeOptions/customizeOptions';
import { MaybeRating } from './maybe-rating';
import { useGetImage } from './use-get-image';
import { useGTM } from './use-gtm';

import HoverPrefetchLink from '../hover-prefetch-link';
import HoverQuickViewLazyLoad from '../hover-quick-view-lazy-load';

export const GalleryItem = ({
  item,
  showReview = true,
  showCustomizeOptions = true,
  sizes,
  imgPriority = false,
}: {
  item: ConfigurableProduct;
  showReview?: boolean;
  showCustomizeOptions?: boolean;
  sizes?: string;
  imgPriority?: boolean;
}) => {
  const { hoverImage, productImage, selectedColor, setSelectedColor } = useGetImage({
    item,
  });
  const allColorCount = item?.show_colors?.total;

  const colorOptions = useMemo(() => {
    if (item.show_colors?.items?.length) {
      return item.show_colors.items;
    }

    return [];
  }, [item]);

  const { handleLinkClick } = useGTM({
    item,
  });

  const { name, price_range, url_key, rating_summary, review_count } = item || {};

  const productLink = resourceUrl(`/${url_key}.html`);
  const priceSource = price_range.minimum_price.final_price || price_range.minimum_price.regular_price;
  const { configurable_options } = item || {};
  const { isHasPreOrder } = useIsPreorder({
    product: item,
    configurable_options,
  });
  const outOfStock = useMemo(() => {
    if (isHasPreOrder) {
      return false;
    }

    if (item.stock_status === 'OUT_OF_STOCK') {
      return true;
    }
  }, [isHasPreOrder, item.stock_status]);

  const isPreOrder = item?.presale_product?.status;

  const setCartStatus = useMiniCartStore(state => state?.setCartStatus);

  if (!item) {
    return null;
  }

  return (
    <div className='relative flex flex-col'>
      <div className='group relative mb-[10px] lg_mb-[20px]'>
        <HoverPrefetchLink
          onClick={handleLinkClick}
          href={productLink}
          className={'relative block w-full overflow-hidden'}
        >
          <AspectRatio ratio={1}>
            {productImage && (
              <Image
                src={productImage}
                className='duration-300 w-full transition-opacity lg_group-hover_opacity-0'
                alt={name ?? ''}
                placeholder={placeHolderImage}
                unoptimized
                priority={imgPriority}
                width={320}
                height={320}
              />
            )}
            {hoverImage && (
              <div className='duration-300 absolute inset-0 opacity-0 transition-opacity lg_group-hover_opacity-100'>
                <Image
                  src={hoverImage}
                  className='w-full'
                  alt={name ?? ''}
                  placeholder={placeHolderImage}
                  unoptimized
                  priority={imgPriority}
                  width={320}
                  height={320}
                />
              </div>
            )}
          </AspectRatio>
        </HoverPrefetchLink>
        <div className='absolute inset-x-[15px] bottom-[15px] hidden lg_block'>
          <HoverQuickViewLazyLoad
            newUrlKey={item?.url_key ?? ''}
            afterSubmit={() => {
              setCartStatus('addtocart');
            }}
          />
        </div>
      </div>

      {showCustomizeOptions && colorOptions?.length > 0 ? (
        <CustomizeOptions
          allColorCount={allColorCount ?? 0}
          colorOptions={colorOptions}
          selectedColor={selectedColor}
          setSelectedColor={setSelectedColor}
          outOfStock={outOfStock}
          url_key={url_key ?? ''}
        />
      ) : null}
      <HoverPrefetchLink
        style={{
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 2,
        }}
        onClick={handleLinkClick}
        href={productLink}
        className='mb-[5px] overflow-hidden font-sans text-sm leading-[1.4] text-title hover_text-primary lg_text-[16px]'
      >
        {formatString(name ?? '')}
      </HoverPrefetchLink>

      <div className='mb-[10px] flex items-center justify-start'>
        <div className='font-poppins text-[16px] font-[600] leading-[1.4] text-title lg_text-[18px]'>
          <Price
            value={priceSource.value}
            currencyCode={priceSource.currency}
          />
        </div>

        {outOfStock ? (
          <div className='ml-[10px] w-[100px] rounded-[20px] bg-[#B52F1D] text-center font-sans text-sm leading-[20px] text-white'>
            Out Of Stock
          </div>
        ) : null}
      </div>

      <MaybeRating
        rating_summary={rating_summary}
        review_count={review_count}
        showReview={showReview}
      />

      {!isPreOrder && (
        <>
          <MaybeNew productMark={item?.product_mark || ''} />
          <MaybeSale productMark={item?.product_mark || ''} />
          <MaybeBestSeller productMark={item?.product_mark || ''} />
        </>
      )}
      <MaybePreOrder isPreOrder={isPreOrder} />
      <div className='mt-[10px] lg_hidden'>
        <HoverQuickViewLazyLoad
          newUrlKey={item?.url_key ?? ''}
          afterSubmit={() => {
            setCartStatus('addtocart');
          }}
        />
      </div>
    </div>
  );
};

function MaybePreOrder({ isPreOrder }: { isPreOrder: boolean | null | undefined }) {
  if (!isPreOrder) {
    return null;
  }

  return (
    <div className='absolute left-[6px] top-[10px] rounded-[20px] border-1 border-primary bg-white px-[12px] font-sans text-sm leading-[24px] text-primary'>
      Pre-Order
    </div>
  );
}

function MaybeNew({ productMark }: { productMark: string }) {
  if (!productMark.toLowerCase().includes('new')) {
    return null;
  }

  return (
    <div className='absolute left-[6px] top-[10px] rounded-[20px] border-1 border-primary bg-white px-[12px] font-sans text-sm leading-[24px] text-primary'>
      New
    </div>
  );
}

function MaybeBestSeller({ productMark }: { productMark: string }) {
  if (!productMark.toLowerCase().includes('best seller')) {
    return null;
  }

  return (
    <div className='absolute left-[6px] top-[10px] rounded-[20px] border-1 border-white bg-primary px-[12px] font-sans text-sm leading-[24px] text-white'>
      Best Seller
    </div>
  );
}

function MaybeSale({ productMark }: { productMark: string }) {
  if (!productMark.toLowerCase().includes('sale')) {
    return null;
  }

  return (
    <div className='absolute left-[6px] top-[10px] rounded-[20px] border-1 border-primary bg-white px-[12px] font-sans text-sm leading-[24px] text-primary'>
      Sale
    </div>
  );
}
